// Editor ÚNICO de templates (§4.5), usado em 2 contextos (§2):
// - "free"  → /notification-center/templates/:templateUuid
//   (cria versão, valida, simula; mostra onde está plugado);
// - "event" → …/configurations/:wl/template-configuration/:id
//   (mostra qual evento/WL usa o template).
//
// Abas de trabalho: [Código | Preview | Dados de exemplo] —
// CodeMirror + chips de variáveis · preview ao vivo em largura total ·
// JSON de exemplo do payload (paths das variables) · dropdown de
// versões (lifecycle DRAFT/PUBLISHED/ARCHIVED) · diff · rollback (PUT
// activate) · salvar rascunho (POST versions) · publicar (POST+PUT
// encadeados) · validação de sintaxe/variáveis · test-send.
import {
  GitCompareIcon,
  MailIcon,
  RotateCcwIcon,
  SendIcon,
} from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { WhatsAppIcon } from "@/components/icons/whatsapp"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"

import { useNotificationCenterTemplateVersions } from "../hooks"
import {
  getNextTemplateVersion,
  useActivateTemplateVersion,
  usePublishTemplateVersion,
  useSaveTemplateDraft,
} from "../template-editor-mutations"
import {
  applySampleOverrides,
  buildExampleObjectFromVariables,
  findUndeclaredVariables,
  formatHtmlSource,
  formatJsonSource,
  validateHtmlSource,
  validateJsonSource,
} from "../template-editor-utils"
import { JsonViewer } from "./json-viewer"
import { TemplateAiDialog } from "./template-ai-dialog"
import { TemplateDiffDialog } from "./template-diff-dialog"
import { TemplatePreview } from "./template-preview"
import { TemplateSampleDataEditor } from "./template-sample-data-editor"
import type { TemplateSourceEditorHandle } from "./template-source-editor"
import { TemplateSourceEditor } from "./template-source-editor"
import { TemplateTestSendDialog } from "./template-test-send-dialog"
import { TemplateVariablesPanel } from "./template-variables-panel"
import {
  getVersionStatusLabel,
  TemplateActiveVersionBadge,
  TemplateVersionStatusBadge,
} from "./template-version-badge"

const editorPrefix =
  "pages.notificationCenter.templateEditorPage"

const WorkspaceSkeleton = () => (
  <div className="space-y-4">
    <Skeleton className="h-8 w-64" />
    <Skeleton className="h-10 w-full" />
    <div className="grid gap-4 @4xl:grid-cols-2">
      <Skeleton className="h-96 w-full" />
      <Skeleton className="h-96 w-full" />
    </div>
  </div>
)

export type TemplateEditorWorkspaceProps = {
  templateUuid: string
  context: "free" | "event"
  whiteLabelId?: string
}

export const TemplateEditorWorkspace = ({
  templateUuid,
  context,
  whiteLabelId,
}: TemplateEditorWorkspaceProps) => {
  const { t } = useTranslation()

  // Criação de template acontece no dialog (POST /templates →
  // uuid real); aqui SEMPRE lemos as versões do uuid da rota —
  // o cache pode já vir semeado pela mutation de criação.
  const { data, error, isLoading } =
    useNotificationCenterTemplateVersions(templateUuid)

  const authorLabel = t(`${editorPrefix}.authorYou`)
  const saveDraftMutation = useSaveTemplateDraft(
    templateUuid,
    authorLabel
  )
  const publishMutation = usePublishTemplateVersion(
    templateUuid,
    authorLabel
  )
  const activateMutation =
    useActivateTemplateVersion(templateUuid)

  // Versão aberta + source em edição (null = sem mudanças locais).
  const [selectedVersion, setSelectedVersion] = useState<
    number | null
  >(null)
  const [draftSource, setDraftSource] = useState<
    string | null
  >(null)
  // Edições locais dos "Dados de exemplo" (por chave da variável) —
  // valem só para o preview; não persistem no template.
  const [sampleOverrides, setSampleOverrides] = useState<
    Record<string, string>
  >({})
  const [diffOpen, setDiffOpen] = useState(false)
  const [testSendOpen, setTestSendOpen] = useState(false)
  const [publishConfirmOpen, setPublishConfirmOpen] =
    useState(false)
  const [activateConfirmOpen, setActivateConfirmOpen] =
    useState(false)
  const editorRef = useRef<TemplateSourceEditorHandle>(null)

  const versions = data?.versions ?? []
  const activeVersion = versions.find((item) => item.active)

  // Abre a versão ATIVA por padrão (fallback: a mais recente).
  useEffect(() => {
    if (data && selectedVersion === null) {
      const initial =
        activeVersion?.version ??
        versions[0]?.version ??
        null
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedVersion(initial)
    }
  }, [data, selectedVersion, activeVersion, versions])

  if (isLoading) return <WorkspaceSkeleton />

  if (error) {
    return (
      <p className="rounded-md border border-red-200 bg-red-50 p-4 text-center text-sm text-red-700">
        {t(`${editorPrefix}.loadError`)}
      </p>
    )
  }

  if (!data || versions.length === 0) {
    return (
      <p className="rounded-md border p-8 text-center text-sm text-muted-foreground">
        {t(`${editorPrefix}.emptyState`)}
      </p>
    )
  }

  const selected =
    versions.find(
      (item) => item.version === selectedVersion
    ) ?? versions[0]
  const displayedSource =
    draftSource ?? selected?.source ?? ""
  const isDirty =
    draftSource !== null &&
    draftSource !== (selected?.source ?? "")

  const language =
    data.channel === "EMAIL" ? "html" : "json"
  const htmlValidation =
    language === "html"
      ? validateHtmlSource(displayedSource)
      : null
  const jsonValidation =
    language === "json"
      ? validateJsonSource(displayedSource)
      : null
  const undeclaredVariables = findUndeclaredVariables(
    displayedSource,
    data.variables
  )
  const nextVersion = getNextTemplateVersion(versions)

  // Variáveis com os exemplos editados aplicados — alimentam o preview
  // (HTML/WhatsApp) e o JSON interpolado.
  const effectiveVariables = applySampleOverrides(
    data.variables,
    sampleOverrides
  )

  const handleSampleChange = (key: string, value: string) =>
    setSampleOverrides((prev) => ({
      ...prev,
      [key]: value,
    }))

  const handleSampleReset = () => setSampleOverrides({})

  const handleSelectVersion = (value: string) => {
    setSelectedVersion(Number(value))
    setDraftSource(null)
  }

  const handleSaveDraft = () => {
    saveDraftMutation.mutate({ source: displayedSource })
    setSelectedVersion(nextVersion)
    setDraftSource(null)
  }

  const handlePublish = () => {
    publishMutation.mutate({
      source: displayedSource,
      nextVersion,
    })
    setSelectedVersion(nextVersion)
    setDraftSource(null)
    setPublishConfirmOpen(false)
  }

  const handleActivate = () => {
    if (selected) {
      activateMutation.mutate({
        version: selected.version,
      })
    }
    setActivateConfirmOpen(false)
  }

  const handleFormatJson = () => {
    const formatted = formatJsonSource(displayedSource)
    if (formatted !== null) {
      setDraftSource(formatted)
    }
  }

  const handleFormatHtml = () => {
    const formatted = formatHtmlSource(displayedSource)
    if (formatted !== null) {
      setDraftSource(formatted)
    }
  }

  const handleInsertVariable = (name: string) => {
    editorRef.current?.insertAtCursor(`{{:${name}}}`)
    // Garante dirty mesmo se o CodeMirror não montar (testes).
    if (draftSource === null) {
      setDraftSource(displayedSource)
    }
  }

  const channelTabLabel =
    data.channel === "EMAIL"
      ? t(`${editorPrefix}.channelTab.EMAIL`)
      : data.channel === "WHATSAPP"
        ? t(`${editorPrefix}.channelTab.WHATSAPP`)
        : data.channel

  return (
    <div className="space-y-4 pb-20 @container">
      {/* ── Cabeçalho: nome, contexto e "plugado em" ─────────────── */}
      <header className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-xl font-bold tracking-tight">
            {data.name}
          </h2>
          {activeVersion && (
            <span className="text-sm tabular-nums text-muted-foreground">
              {t(`${editorPrefix}.activeVersionLabel`, {
                version: activeVersion.version,
              })}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          {context === "event" && (
            <Badge
              variant="outline"
              className="rounded-full border border-violet-200 bg-violet-50 font-medium text-violet-700"
            >
              {t(`${editorPrefix}.eventContext`)}
            </Badge>
          )}
          {whiteLabelId && (
            <span>
              {t(`${editorPrefix}.whiteLabelLabel`)}:{" "}
              {whiteLabelId}
            </span>
          )}
          <span className="flex flex-wrap items-center gap-1">
            {t(`${editorPrefix}.usedByLabel`)}:
            {data.usedBy.length === 0 ? (
              <Badge
                variant="outline"
                className="rounded-full border border-slate-200 bg-slate-50 font-medium text-slate-600"
              >
                {t(`${editorPrefix}.notAssociated`)}
              </Badge>
            ) : (
              data.usedBy.map((usage) => (
                <Badge
                  key={`${usage.event}-${usage.schedulerUuid ?? "immediate"}`}
                  variant="outline"
                  className="rounded-full border border-violet-200 bg-violet-50 font-medium text-violet-700"
                >
                  {usage.label}
                </Badge>
              ))
            )}
          </span>
        </div>
      </header>

      {/* ── Tabs por canal do template ───────────────────────────── */}
      <Tabs value={data.channel}>
        <TabsList>
          <TabsTrigger value={data.channel}>
            {data.channel === "WHATSAPP" ? (
              <WhatsAppIcon
                className="mr-2 size-4"
                aria-hidden="true"
              />
            ) : (
              <MailIcon
                className="mr-2 size-4"
                aria-hidden="true"
              />
            )}
            {channelTabLabel}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* ── Toolbar de versões ───────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2">
        <label
          className="text-sm text-muted-foreground"
          htmlFor="template-version-select"
        >
          {t(`${editorPrefix}.versions.label`)}
        </label>
        <Select
          value={
            selected ? String(selected.version) : undefined
          }
          onValueChange={handleSelectVersion}
        >
          <SelectTrigger
            id="template-version-select"
            className="w-64"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {versions.map((item) => (
              <SelectItem
                key={item.version}
                value={String(item.version)}
              >
                <span className="flex items-center gap-2">
                  <span className="tabular-nums">
                    v{item.version}
                  </span>
                  <TemplateVersionStatusBadge
                    status={item.status}
                    label={getVersionStatusLabel(
                      t,
                      item.status
                    )}
                  />
                  {item.active && (
                    <TemplateActiveVersionBadge
                      label={t(
                        `${editorPrefix}.versions.active`
                      )}
                    />
                  )}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selected && (
          <span className="text-xs text-muted-foreground">
            {t(`${editorPrefix}.versions.author`, {
              author: selected.author || "—",
            })}
          </span>
        )}
        <div className="ml-auto flex flex-wrap gap-2">
          {/* Melhorar com IA (ação, não uma aba): gera uma sugestão
              e, se aplicada, cai no editor como rascunho (dirty). */}
          <TemplateAiDialog
            templateUuid={templateUuid}
            source={displayedSource}
            channel={data.channel}
            variables={data.variables}
            onApply={setDraftSource}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={versions.length < 2}
            onClick={() => setDiffOpen(true)}
          >
            <GitCompareIcon
              className="mr-1 size-4"
              aria-hidden="true"
            />
            {t(`${editorPrefix}.diff.button`)}
          </Button>
          {selected && !selected.active && !isDirty && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setActivateConfirmOpen(true)}
            >
              <RotateCcwIcon
                className="mr-1 size-4"
                aria-hidden="true"
              />
              {t(`${editorPrefix}.versions.activate`)}
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setTestSendOpen(true)}
          >
            <SendIcon
              className="mr-1 size-4"
              aria-hidden="true"
            />
            {t(`${editorPrefix}.testSend.button`)}
          </Button>
        </div>
      </div>

      {/* ── Abas de trabalho: Código | Preview | Dados de exemplo ──
          (edição de e-mail é HTML cru + preview ao vivo; WYSIWYG
          degradava o markup de e-mail, então foi removido). ── */}
      <Tabs defaultValue="code" className="space-y-4">
        <TabsList>
          <TabsTrigger value="code">
            {t(`${editorPrefix}.tabs.code`)}
          </TabsTrigger>
          <TabsTrigger value="preview">
            {t(`${editorPrefix}.tabs.preview`)}
          </TabsTrigger>
          <TabsTrigger value="sampleData">
            {t(`${editorPrefix}.tabs.sampleData`)}
          </TabsTrigger>
        </TabsList>

        {/* Código: editor + chips de variáveis + validação. */}
        <TabsContent value="code" className="space-y-3">
          <TemplateSourceEditor
            ref={editorRef}
            value={displayedSource}
            onChange={setDraftSource}
            language={language}
            aria-label={t(
              `${editorPrefix}.sourceEditorLabel`
            )}
          />
          <TemplateVariablesPanel
            variables={data.variables}
            onInsert={handleInsertVariable}
          />
          {/* Rodapé de validação (sintaxe + variáveis). */}
          <div
            className="space-y-1 rounded-md border p-3 text-xs"
            data-testid="template-validation-footer"
          >
            <h3 className="font-semibold">
              {t(`${editorPrefix}.validation.title`)}
            </h3>
            {htmlValidation && (
              <div className="flex items-center justify-between gap-2">
                {htmlValidation.valid ? (
                  <p className="text-emerald-700">
                    {t(`${editorPrefix}.validation.htmlOk`)}
                  </p>
                ) : (
                  <p className="text-red-700" role="alert">
                    {t(
                      `${editorPrefix}.validation.htmlIssues`,
                      {
                        issues:
                          htmlValidation.issues.join(", "),
                      }
                    )}
                  </p>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleFormatHtml}
                >
                  {t(`${editorPrefix}.validation.format`)}
                </Button>
              </div>
            )}
            {jsonValidation && (
              <div className="flex items-center justify-between gap-2">
                {jsonValidation.valid ? (
                  <p className="text-emerald-700">
                    {t(`${editorPrefix}.validation.jsonOk`)}
                  </p>
                ) : (
                  <p className="text-red-700" role="alert">
                    {jsonValidation.line !== null
                      ? t(
                          `${editorPrefix}.validation.jsonError`,
                          { line: jsonValidation.line }
                        )
                      : t(
                          `${editorPrefix}.validation.jsonErrorNoLine`
                        )}
                  </p>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleFormatJson}
                >
                  {t(`${editorPrefix}.validation.format`)}
                </Button>
              </div>
            )}
            {undeclaredVariables.length > 0 && (
              <p
                className="text-amber-700"
                role="alert"
                data-testid="undeclared-variables"
              >
                ⚠{" "}
                {t(`${editorPrefix}.validation.undeclared`)}{" "}
                {undeclaredVariables
                  .map((name) => `{{:${name}}}`)
                  .join(", ")}
              </p>
            )}
          </div>
        </TabsContent>

        {/* Preview: HTML/JSON sanitizado, LADO A LADO com os dados de
            exemplo — o usuário vê o payload sendo interpolado ({{:campo}}
            → valor). Em telas menores, empilha. */}
        <TabsContent value="preview">
          {/* Preview à esquerda, "Dados interpolados" fixos à direita
              (lado a lado). Usa CONTAINER-query (@3xl) — e não o
              breakpoint de viewport (lg:) — porque o editor vive dentro
              de um @container; com lg: os dados empilhavam mesmo com o
              editor largo. Empilha só em containers realmente estreitos. */}
          <div className="grid grid-cols-1 gap-4 @3xl:grid-cols-[minmax(0,1fr)_20rem]">
            <TemplatePreview
              channel={data.channel}
              source={displayedSource}
              variables={effectiveVariables}
            />
            {data.variables.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground">
                  {t(`${editorPrefix}.previewSampleTitle`)}
                </p>
                <JsonViewer
                  value={buildExampleObjectFromVariables(
                    effectiveVariables
                  )}
                  fileName="sample-data.json"
                />
              </div>
            )}
          </div>
        </TabsContent>

        {/* Dados de exemplo: EDITÁVEL — o usuário sobrescreve os
            valores de exemplo de cada variável ({{:campo}}) para ver o
            preview com dados próprios. As edições valem só para o
            preview (não persistem no template). */}
        <TabsContent
          value="sampleData"
          className="space-y-2"
        >
          {data.variables.length === 0 ? (
            <p className="rounded-md border p-4 text-sm text-muted-foreground">
              {t(`${editorPrefix}.variables.empty`)}
            </p>
          ) : (
            <TemplateSampleDataEditor
              variables={data.variables}
              overrides={sampleOverrides}
              onChange={handleSampleChange}
              onReset={handleSampleReset}
            />
          )}
        </TabsContent>
      </Tabs>

      {/* ── Footer de dirty-state ────────────────────────────────── */}
      {isDirty && (
        <footer
          className="mt-2 rounded-md border bg-muted/30 p-3"
          data-testid="dirty-footer"
        >
          <div className="flex flex-wrap items-center justify-end gap-2">
            <span className="mr-auto text-sm text-muted-foreground">
              {t(`${editorPrefix}.footer.unsaved`)}
            </span>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDraftSource(null)}
            >
              {t(`${editorPrefix}.footer.cancel`)}
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={saveDraftMutation.isPending}
              onClick={handleSaveDraft}
            >
              {t(`${editorPrefix}.footer.saveDraft`)}
            </Button>
            <Button
              type="button"
              className="btn-whitelabel"
              disabled={publishMutation.isPending}
              onClick={() => setPublishConfirmOpen(true)}
            >
              {t(`${editorPrefix}.footer.publish`)}
            </Button>
          </div>
        </footer>
      )}

      {/* ── AlertDialog: publicar vN e desativar vM ──────────────── */}
      <AlertDialog
        open={publishConfirmOpen}
        onOpenChange={setPublishConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(`${editorPrefix}.footer.publishTitle`, {
                next: nextVersion,
                current: activeVersion?.version ?? "—",
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                `${editorPrefix}.footer.publishDescription`,
                {
                  next: nextVersion,
                  current: activeVersion?.version ?? "—",
                }
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t(`${editorPrefix}.footer.cancel`)}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handlePublish}>
              {t(`${editorPrefix}.footer.publish`)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── AlertDialog: rollback (ativar versão aberta) ─────────── */}
      <AlertDialog
        open={activateConfirmOpen}
        onOpenChange={setActivateConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(`${editorPrefix}.versions.activateTitle`, {
                version: selected?.version,
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                `${editorPrefix}.versions.activateDescription`,
                {
                  current: activeVersion?.version ?? "—",
                }
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t(`${editorPrefix}.footer.cancel`)}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleActivate}>
              {t(`${editorPrefix}.versions.activate`)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Montado só quando aberto: o "comparar com" inicial reflete
          a versão aberta no momento do clique. */}
      {selected && diffOpen && (
        <TemplateDiffDialog
          open={diffOpen}
          onOpenChange={setDiffOpen}
          currentVersion={selected.version}
          currentSource={displayedSource}
          versions={versions}
        />
      )}
      {/* Montado só quando aberto: os defaultValues do form capturam
          a versão SELECIONADA no momento do clique. */}
      {selected && testSendOpen && (
        <TemplateTestSendDialog
          open={testSendOpen}
          onOpenChange={setTestSendOpen}
          templateUuid={templateUuid}
          defaultChannel={data.channel}
          defaultVersion={selected.version}
          versions={versions}
        />
      )}
    </div>
  )
}
